import React, { FunctionComponent, memo, useState } from 'react'
import { Button } from 'atoms/Button'
import Dotdotdot from 'react-dotdotdot'
import { cn } from 'ethcss'
import { scheduleItemStyles } from './ScheduleItem-styles'
import helpers from 'core/helpers'
import { IScheduleItemComponent } from './ScheduleItem-types'
import { useConfirmDelete, useGetBroadcastOnSelect, useScheduleDevicesPreview } from './ScheduleItem-hooks'
import { setEditBroadcastLocation } from '../../pages/broadcasts/broadcasts.local.methods'
import translate from 'core/translate'
import { IBroadcast } from 'core/models/Broadcast'
import { getWordTranslateWithDeclension } from 'core/utils/coreUtil'
import Icon from 'blocks.simple/icon/icon'
import { ScheduleDevicesModal } from './ScheduleDevicesModal'

const PriorityType = {
    0: 'Low',
    1: 'Medium',
    2: 'High',
}

const ScheduleItemComponent: FunctionComponent<IScheduleItemComponent> = ({
    id,
    broadcastId,
    isShowButtons = true,
    timeTitle,
    broadcastTitle,
    onDeleteClick,
    onSelectClick,
    onEditClick,
    selected,
    devicesCount,
    setEvents,
    priority,
}) => {
    let { isConfirmDelete, setConfirmDelete } = useConfirmDelete()
    let { broadcast } = useGetBroadcastOnSelect(broadcastId, selected)
    const [showDevicesModal, setShowDevicesModal] = useState(false)
    const { total: previewTotal } = useScheduleDevicesPreview(id, selected, devicesCount)

    const devicesTotal = previewTotal || devicesCount || 0

    const handleScheduleEdit = () => {
        if (broadcast) {
            setEditBroadcastLocation(broadcast, broadcast.folderId)
        }
    }

    const handleCancelDelete = () => {
        setConfirmDelete(false)
    }

    const handleDelete = (e: unknown) => {
        if (!isConfirmDelete) {
            return setConfirmDelete(true)
        }

        onDeleteClick(e)
        setConfirmDelete(false)
        if (setEvents && id !== undefined) {
            setEvents(id)
        }
    }

    const handleDevicesSearch = () => {
        if (!id) return
        setShowDevicesModal(true)
    }

    const isEnableEditBroadcast = (broadcast: IBroadcast | null) => {
        return helpers.isAvailable({ key: 'schedule', action: 'update', strict: true })
    }

    const getDevicesListText = () => {
        let translation = getWordTranslateWithDeclension(devicesTotal, [
            translate('device'),
            translate('devices'),
            translate('devices|'),
        ]).toLowerCase()

        return `${devicesTotal} ${translation}`
    }

    const priorityString = PriorityType[priority as 0 | 1 | 2]

    return (
        <div className={'test-schedule-details'}>
            {selected && (
                <div className={scheduleItemStyles.selected}>
                    {!isConfirmDelete && (
                        <div
                            className={cn(scheduleItemStyles.timeTitleContainer, {
                                [scheduleItemStyles.timeTitleContainerMarginBottomNormal]: isShowButtons,
                            })}
                        >
                            {
                                <div className={scheduleItemStyles.eventContent}>
                                    <div
                                        className={cn(
                                            scheduleItemStyles.priority,
                                            scheduleItemStyles[`priority${priorityString}`]
                                        )}
                                    ></div>
                                    <div>
                                        {translate('priority')}:{' '}
                                        {translate(`${priorityString.toLocaleLowerCase()}Priority`)}
                                    </div>
                                </div>
                            }
                            <div className={scheduleItemStyles.timeTitle}>{timeTitle}</div>
                            <div>{broadcastTitle}</div>
                            {devicesTotal > 0 ? (
                                <div className={scheduleItemStyles.linkNoPb} onClick={handleDevicesSearch}>
                                    {getDevicesListText()}
                                </div>
                            ) : null}
                        </div>
                    )}
                    <div className={scheduleItemStyles.controlsButtonsContainer}>
                        {!isConfirmDelete && isEnableEditBroadcast(broadcast) ? (
                            <div className={scheduleItemStyles.controlsLeftContainer}>
                                <Icon
                                    type={'editBroadcast'}
                                    size={24}
                                    color={'custom'}
                                    onClick={handleScheduleEdit}
                                    tooltip={{ title: translate('edit_broadcast') }}
                                />
                            </div>
                        ) : null}
                        {isShowButtons && (
                            <div className={scheduleItemStyles.controlsContainer}>
                                {helpers.isAvailable({ key: 'schedule', action: 'update', strict: true }) &&
                                    !isConfirmDelete && (
                                        <div className={cn(scheduleItemStyles.buttonContainerCenterItem)}>
                                            <Icon
                                                type={'pencil'}
                                                size={18}
                                                color={'custom'}
                                                onClick={onEditClick}
                                                tooltip={{ title: translate('editSchedule') }}
                                            />
                                        </div>
                                    )}
                                {!isConfirmDelete &&
                                    helpers.isAvailable({ key: 'schedule', action: 'delete', strict: true }) && (
                                        <div className={cn(scheduleItemStyles.buttonContainer)}>
                                            <Icon
                                                type={'deleteBasket'}
                                                size={18}
                                                color={'custom'}
                                                onClick={handleDelete}
                                                tooltip={{ title: translate('delete') }}
                                            />
                                        </div>
                                    )}
                                {isConfirmDelete &&
                                    helpers.isAvailable({ key: 'schedule', action: 'delete', strict: true }) && (
                                        <div className={cn(scheduleItemStyles.buttonContainer)}>
                                            <Button
                                                indent={'normal'}
                                                rounded={'full_normal'}
                                                onClick={handleDelete}
                                                className={cn(scheduleItemStyles.button)}
                                                contentClassName={scheduleItemStyles.buttonContent}
                                            >
                                                <span>{translate('yes')}</span>
                                            </Button>
                                        </div>
                                    )}
                                {isConfirmDelete && (
                                    <div className={cn(scheduleItemStyles.buttonContainer)}>
                                        <Button
                                            indent={'normal'}
                                            rounded={'full_normal'}
                                            // fullWidth={true}
                                            onClick={handleCancelDelete}
                                            className={cn(scheduleItemStyles.button)}
                                            contentClassName={scheduleItemStyles.buttonContent}
                                        >
                                            <span>{translate('no')}</span>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {isConfirmDelete && (
                        <div className={scheduleItemStyles.confirmTitle}>
                            <Dotdotdot clamp={2}>
                                <span dangerouslySetInnerHTML={{ __html: translate('youAreSureToDeleteSchedule') }} />{' '}
                                {timeTitle} {broadcastTitle}?
                            </Dotdotdot>
                        </div>
                    )}
                </div>
            )}
            {!selected && (
                <div className={cn(scheduleItemStyles.scheduleItem, 'test-broad-schedule')} onClick={onSelectClick}>
                    <div className={scheduleItemStyles.timeTitle}>{timeTitle}</div>
                    <div className={scheduleItemStyles.dotdotdotContainer}>
                        <Dotdotdot clamp={2}>
                            <div className={scheduleItemStyles.broadcastTitle}>{broadcastTitle}</div>
                        </Dotdotdot>
                    </div>
                </div>
            )}
            {showDevicesModal && id ? (
                <ScheduleDevicesModal scheduleId={id} total={devicesTotal} onClose={() => setShowDevicesModal(false)} />
            ) : null}
        </div>
    )
}

export const ScheduleItem = memo(ScheduleItemComponent)
